import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { sanitizeUrl } from '../../../../services/urlSafety';
import styles from './GameMediaGallery.module.css';
import { GameMediaGalleryProps } from './GameMediaGallery.types';

export const GameMediaGallery: React.FC<GameMediaGalleryProps> = ({ media, gameTitle }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'video' | 'image'>('all');
  const [displayedFilter, setDisplayedFilter] = useState<'all' | 'video' | 'image'>('all');
  const [isTabTransitioning, setIsTabTransitioning] = useState(false);
  const [activeMediaId, setActiveMediaId] = useState<string>(media?.[0]?.id || '');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const thumbnailsRowRef = useRef<HTMLDivElement>(null);
  const tabTransitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hasVideos = media?.some((item) => item.type === 'video') ?? false;
  const hasImages = media?.some((item) => item.type === 'image') ?? false;
  const showFilterTabs = hasVideos && hasImages;

  const filteredMedia = (media || []).filter((item) => {
    if (!showFilterTabs || displayedFilter === 'all') return true;
    return item.type === displayedFilter;
  });

  const activeItem =
    filteredMedia.find((item) => item.id === activeMediaId) || filteredMedia[0] || media?.[0];

  const currentIndex = filteredMedia.findIndex((item) => item.id === activeItem?.id);
  const activeIndex = currentIndex >= 0 ? currentIndex : 0;

  const scrollToThumbnail = (id: string) => {
    if (thumbnailsRowRef.current) {
      const activeBtn = Array.from(
        thumbnailsRowRef.current.querySelectorAll<HTMLButtonElement>('[data-thumb-id]')
      ).find((btn) => btn.dataset.thumbId === id);
      if (activeBtn && typeof activeBtn.scrollIntoView === 'function') {
        activeBtn.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  };

  const handleSelectMedia = (id: string) => {
    setActiveMediaId(id);
    scrollToThumbnail(id);
  };

  const handlePrevMedia = () => {
    if (filteredMedia.length <= 1) return;
    const prevIndex = (activeIndex - 1 + filteredMedia.length) % filteredMedia.length;
    const prevItem = filteredMedia[prevIndex];
    if (prevItem) {
      handleSelectMedia(prevItem.id);
    }
  };

  const handleNextMedia = () => {
    if (filteredMedia.length <= 1) return;
    const nextIndex = (activeIndex + 1) % filteredMedia.length;
    const nextItem = filteredMedia[nextIndex];
    if (nextItem) {
      handleSelectMedia(nextItem.id);
    }
  };

  const handleTabChange = useCallback(
    (newFilter: 'all' | 'video' | 'image') => {
      if (newFilter === filter) return;

      if (tabTransitionTimeoutRef.current) {
        clearTimeout(tabTransitionTimeoutRef.current);
      }

      setFilter(newFilter);
      setIsTabTransitioning(true);

      tabTransitionTimeoutRef.current = setTimeout(() => {
        setDisplayedFilter(newFilter);
        const newFiltered = (media || []).filter((item) => {
          if (newFilter === 'all') return true;
          return item.type === newFilter;
        });
        if (newFiltered.length > 0) {
          setActiveMediaId(newFiltered[0].id);
        }
        setIsTabTransitioning(false);
      }, 400);
    },
    [filter, media]
  );

  useEffect(() => {
    return () => {
      if (tabTransitionTimeoutRef.current) {
        clearTimeout(tabTransitionTimeoutRef.current);
      }
    };
  }, []);

  const [prevMedia, setPrevMedia] = useState(media);
  if (media !== prevMedia) {
    setPrevMedia(media);
    setFilter('all');
    setDisplayedFilter('all');
    setIsTabTransitioning(false);
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        setIsLightboxOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen]);

  if (!media || media.length === 0) return null;

  return (
    <section className={styles.galleryContainer} aria-labelledby="gallery-heading">
      <div className={styles.headerRow}>
        <div className={styles.titleArea}>
          <h2 id="gallery-heading" className={styles.galleryTitle}>
            {t.games.media.title}
          </h2>
          <p className={styles.gallerySubtitle}>{t.games.media.subtitle}</p>
        </div>

        {showFilterTabs && (
          <div className={styles.filterTabs} role="tablist" aria-label={t.games.media.filterLabel}>
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'all'}
              onClick={() => handleTabChange('all')}
              className={`${styles.tabBtn} ${filter === 'all' ? styles.tabBtnActive : ''}`}
            >
              {t.games.media.allTab}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'video'}
              onClick={() => handleTabChange('video')}
              className={`${styles.tabBtn} ${filter === 'video' ? styles.tabBtnActive : ''}`}
            >
              {t.games.media.videoTab}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'image'}
              onClick={() => handleTabChange('image')}
              className={`${styles.tabBtn} ${filter === 'image' ? styles.tabBtnActive : ''}`}
            >
              {t.games.media.screenshotsTab}
            </button>
          </div>
        )}
      </div>

      <div
        className={`${styles.tabContent} ${
          isTabTransitioning ? styles.tabContentFadeOut : styles.tabContentFadeIn
        }`}
        data-testid="gallery-tab-content"
      >
        {activeItem && (
          <div className={styles.viewportWrapper}>
            {filteredMedia.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevMedia}
                  className={`${styles.navArrowBtn} ${styles.navArrowPrev}`}
                  aria-label={t.games.media.prevMedia}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleNextMedia}
                  className={`${styles.navArrowBtn} ${styles.navArrowNext}`}
                  aria-label={t.games.media.nextMedia}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </>
            )}

            {activeItem.type === 'video' ? (
              <iframe
                key={activeItem.id}
                src={sanitizeUrl(activeItem.src)}
                title={t.games.media.videoPreviewFor({ title: activeItem.title })}
                className={`${styles.videoIframe} ${styles.mediaFadeIn}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <img
                  key={activeItem.id}
                  src={activeItem.src}
                  alt={t.games.media.imagePreviewAlt({ title: gameTitle, index: activeIndex + 1 })}
                  className={`${styles.imagePreview} ${styles.mediaFadeIn}`}
                />
                <div className={styles.captionBar}>
                  <p className={styles.captionText}>
                    {activeItem.title || activeItem.caption || ''}
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsLightboxOpen(true)}
                    className={styles.fullscreenBtn}
                    aria-label={t.games.media.openFullscreen}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                    <span>{t.games.media.openFullscreen}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Thumbnails row with navigation buttons */}
        <div className={styles.thumbnailsContainer}>
          {filteredMedia.length > 1 && (
            <button
              type="button"
              onClick={handlePrevMedia}
              className={`${styles.thumbNavBtn} ${styles.thumbNavBtnPrev}`}
              aria-label={t.games.media.prevMedia}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}

          <div
            ref={thumbnailsRowRef}
            className={styles.thumbnailsRow}
            role="group"
            aria-label={t.games.media.thumbnailsLabel}
          >
            {filteredMedia.map((item, idx) => (
              <button
                key={item.id}
                data-thumb-id={item.id}
                type="button"
                onClick={() => handleSelectMedia(item.id)}
                className={`${styles.thumbBtn} ${activeItem?.id === item.id ? styles.thumbBtnActive : ''}`}
                aria-label={t.games.media.thumbnailAlt({ title: item.title, index: idx + 1 })}
              >
                <img src={item.thumbnailUrl || item.src} alt="" className={styles.thumbImg} />
                {item.type === 'video' && (
                  <span className={styles.thumbVideoBadge} aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </span>
                )}
              </button>
            ))}
          </div>

          {filteredMedia.length > 1 && (
            <button
              type="button"
              onClick={handleNextMedia}
              className={`${styles.thumbNavBtn} ${styles.thumbNavBtnNext}`}
              aria-label={t.games.media.nextMedia}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {isLightboxOpen && activeItem?.type === 'image' && (
        <div className={styles.lightboxBackdrop}>
          <button
            type="button"
            className={styles.lightboxOverlay}
            onClick={() => setIsLightboxOpen(false)}
            aria-label={t.games.media.dismissViewer}
          />
          <div
            className={styles.lightboxBox}
            role="dialog"
            aria-modal="true"
            aria-label={activeItem.title || gameTitle}
          >
            <button
              type="button"
              onClick={() => setIsLightboxOpen(false)}
              className={styles.lightboxCloseBtn}
              aria-label={t.games.media.closeFullscreen}
            >
              ✕
            </button>
            {filteredMedia.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevMedia}
                  className={`${styles.lightboxNavBtn} ${styles.lightboxNavBtnPrev}`}
                  aria-label={t.games.media.prevMedia}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleNextMedia}
                  className={`${styles.lightboxNavBtn} ${styles.lightboxNavBtnNext}`}
                  aria-label={t.games.media.nextMedia}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </>
            )}
            <img
              src={activeItem.src}
              alt={activeItem.title || gameTitle}
              className={styles.lightboxImg}
            />
          </div>
        </div>
      )}
    </section>
  );
};
